/**
 * CertificateCreatePage — Issue #362.
 *
 * 红线 (AI GUIDE §6):
 *   - 证书号在表单内仅短暂保留；提交即刻经后端 cipher+hash 落库。
 *   - 必备 + 关键岗位证照必须挂到具体内部用户账号；否则后端会以 403 拒绝。
 *   - 表单错误来自后端 4xx；没有"前端假状态"。
 */
import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  HR_CERTIFICATE_CRITICAL_ROLE_LABELS,
  useHrCertificateMutations,
  type CreateHrCertificateDto,
} from '@/hooks/useHrCertificates';

const CRITICAL_ROLE_OPTIONS = Object.keys(
  HR_CERTIFICATE_CRITICAL_ROLE_LABELS,
) as Array<NonNullable<CreateHrCertificateDto['criticalRoleKey']>>;

export default function CertificateCreatePage() {
  const { permissions } = useAuth();
  const canWrite = hasPermission(permissions, HR_PERMISSIONS.CERTIFICATE_WRITE);
  const { createCertificate, loading, error } = useHrCertificateMutations();
  const [, navigate] = useLocation();

  const [employeeId, setEmployeeId] = useState('');
  const [userId, setUserId] = useState('');
  const [certificateType, setCertificateType] = useState('');
  const [certificateNo, setCertificateNo] = useState('');
  const [issuingAuthority, setIssuingAuthority] = useState('');
  const [issuedAt, setIssuedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [mandatory, setMandatory] = useState(false);
  const [criticalRoleKey, setCriticalRoleKey] = useState<
    NonNullable<CreateHrCertificateDto['criticalRoleKey']> | ''
  >('');
  const [notes, setNotes] = useState('');

  if (!canWrite) return <AccessDenied resource="新建资质证照" />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const body: CreateHrCertificateDto = {
      employeeId,
      userId: userId || undefined,
      certificateType,
      certificateNo: certificateNo || undefined,
      issuingAuthority: issuingAuthority || undefined,
      issuedAt: issuedAt ? new Date(issuedAt).toISOString() : undefined,
      expiresAt: new Date(expiresAt).toISOString(),
      mandatory,
      criticalRoleKey: criticalRoleKey || undefined,
      notes: notes || undefined,
    };
    const result = await createCertificate(body);
    if (result?.id) {
      navigate(`/hr/certificates/${result.id}`);
    }
  }

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">新建资质证照</h1>
        <p className="text-sm text-slate-500">
          创建后将进入待审核状态。证书号仅以密文 + 后四位 + 单向 hash 落库，
          不会以明文展示或导出。
        </p>
      </header>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error.message}
        </Card>
      ) : null}

      <Card className="p-4">
        <form className="grid grid-cols-2 gap-4" onSubmit={onSubmit}>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">员工 ID (必填)</span>
            <input
              type="text"
              className="rounded border border-slate-300 px-2 py-1"
              value={employeeId}
              required
              onChange={(e) => setEmployeeId(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">关联内部账号 userId (必备证照必填)</span>
            <input
              type="text"
              className="rounded border border-slate-300 px-2 py-1"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">证书类型 (必填)</span>
            <input
              type="text"
              className="rounded border border-slate-300 px-2 py-1"
              value={certificateType}
              placeholder="如 doctor_practice_license"
              required
              onChange={(e) => setCertificateType(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">证书号 (可选，提交后立即加密)</span>
            <input
              type="text"
              className="rounded border border-slate-300 px-2 py-1"
              value={certificateNo}
              onChange={(e) => setCertificateNo(e.target.value)}
              autoComplete="off"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">发证机关</span>
            <input
              type="text"
              className="rounded border border-slate-300 px-2 py-1"
              value={issuingAuthority}
              onChange={(e) => setIssuingAuthority(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">发证日期</span>
            <input
              type="date"
              className="rounded border border-slate-300 px-2 py-1"
              value={issuedAt}
              onChange={(e) => setIssuedAt(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">到期日 (必填)</span>
            <input
              type="date"
              className="rounded border border-slate-300 px-2 py-1"
              value={expiresAt}
              required
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">关键岗位</span>
            <select
              className="rounded border border-slate-300 px-2 py-1"
              value={criticalRoleKey}
              onChange={(e) =>
                setCriticalRoleKey(
                  (e.target.value as typeof criticalRoleKey) || '',
                )
              }
            >
              <option value="">不绑定</option>
              {CRITICAL_ROLE_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {HR_CERTIFICATE_CRITICAL_ROLE_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={mandatory}
              onChange={(e) => setMandatory(e.target.checked)}
            />
            <span>
              该证照为关键岗位必备项 (到期未续期审批通过时将冻结对应账号登录)
            </span>
          </label>
          <label className="col-span-2 flex flex-col gap-1 text-sm">
            <span className="text-slate-600">备注</span>
            <textarea
              className="rounded border border-slate-300 px-2 py-1"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          <div className="col-span-2 flex gap-3 pt-2">
            <button
              type="submit"
              className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white disabled:opacity-50"
              disabled={loading}
            >
              {loading ? '提交中…' : '创建证照'}
            </button>
            <Link
              href="/hr/certificates"
              className="rounded border border-slate-300 px-4 py-1.5 text-sm"
            >
              返回列表
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}
